import { AxiosResponse } from 'axios';
import { createApiClient } from './apiBase';
import {
  Cart,
  CartSummary,
  AddToCartRequest,
  CheckoutRequest,
  Order,
} from '../types';

const api = createApiClient();

// ===== Browse =====

export async function getProducts(params?: {
  search?: string; company_code?: string; category?: string[]; gender?: string[];
  season?: string[]; color?: number[]; min_price?: number; max_price?: number;
  page?: number; page_size?: number;
}): Promise<AxiosResponse<any>> {
  return api.get('/shop/products', {
    params,
    paramsSerializer: (p) => {
      const searchParams = new URLSearchParams();
      Object.entries(p).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, String(v)));
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    },
  });
}

export async function getProductDetail(sku: string): Promise<AxiosResponse<any>> {
  return api.get(`/shop/products/${sku}`);
}

export async function getBrands(): Promise<AxiosResponse<{
  brands: Array<{
    id: number; name: string; slug: string; logo_url?: string;
    header_image_url?: string; description?: string; product_count: number;
  }>; total: number;
}>> {
  return api.get('/shop/brands');
}

export async function getCategories(): Promise<AxiosResponse<{
  categories: Array<{ id: number; name: string; slug: string; display_order: number }>;
  total: number;
}>> {
  return api.get('/shop/categories');
}

export async function getColors(): Promise<AxiosResponse<{
  colors: Array<{ id: number; name: string; hex_code: string }>;
  total: number;
}>> {
  return api.get('/shop/colors');
}

// ===== Delivery =====

export async function getDeliveryInfo(zipCode: string, lat?: number, lng?: number): Promise<AxiosResponse<{
  is_eligible: boolean; zip_code: string; distance_miles: number | null;
  shipping_cost: string | null; eta_min: number | null; eta_max: number | null;
  eta_display: string | null;
  delivery_window: {
    is_same_day: boolean; delivery_date: string; window_start: string; window_end: string;
    display_text: string; countdown_seconds: number | null; cutoff_time: string | null;
  } | null;
  message: string;
}>> {
  return api.get('/shop/delivery-info', { params: { zip_code: zipCode, lat, lng } });
}

export async function getDeliveryStatus(): Promise<AxiosResponse<{
  current_time: string; timezone: string;
  operating_hours: { start: string; end: string; display: string };
  is_open: boolean; same_day_cutoff: string; is_same_day_eligible: boolean;
  delivery_window: {
    is_same_day: boolean; delivery_date: string; window_start: string; window_end: string;
    display_text: string; countdown_seconds: number | null; cutoff_time: string | null;
  };
  next_open: string | null;
}>> {
  return api.get('/shop/delivery-status');
}

export async function checkZipAvailability(zipCode: string): Promise<AxiosResponse<{
  zip_code: string; is_available: boolean; message: string;
}>> {
  return api.get('/shop/check-zip', { params: { zip_code: zipCode } });
}

// ===== Cart =====

export async function getCart(sessionId: string): Promise<AxiosResponse<Cart>> {
  return api.get('/cart/', { headers: { 'X-Session-ID': sessionId } });
}

export async function getCartSummary(sessionId: string): Promise<AxiosResponse<CartSummary>> {
  return api.get('/cart/summary', { headers: { 'X-Session-ID': sessionId } });
}

export async function addToCart(request: AddToCartRequest, sessionId: string): Promise<AxiosResponse<Cart>> {
  return api.post('/cart/add', request, { headers: { 'X-Session-ID': sessionId } });
}

export async function updateCartItem(itemId: number, quantity: number, sessionId: string): Promise<AxiosResponse<Cart>> {
  return api.put(`/cart/items/${itemId}`, { quantity }, { headers: { 'X-Session-ID': sessionId } });
}

export async function removeCartItem(itemId: number, sessionId: string): Promise<AxiosResponse<Cart>> {
  return api.delete(`/cart/items/${itemId}`, { headers: { 'X-Session-ID': sessionId } });
}

export async function clearCart(sessionId: string): Promise<AxiosResponse<{ message: string }>> {
  return api.delete('/cart/clear', { headers: { 'X-Session-ID': sessionId } });
}

// ===== Checkout =====

export async function createOrder(request: CheckoutRequest): Promise<AxiosResponse<Order>> {
  return api.post('/shop/checkout', request);
}

export async function getOrder(orderNumber: string, token?: string): Promise<AxiosResponse<Order>> {
  const params = token ? { token } : {};
  return api.get(`/shop/orders/${orderNumber}`, { params });
}

export async function requestOrderLinks(email: string, orderNumber?: string): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return api.post('/shop/orders/request-link', { email, order_number: orderNumber });
}

export async function lookupOrder(orderNumber: string, email: string): Promise<AxiosResponse<Order>> {
  return api.post('/shop/orders/lookup', { order_number: orderNumber, email });
}

// ===== Customer Auth =====

export async function customerRegister(data: {
  email: string; password: string; first_name: string; last_name: string; phone?: string;
}): Promise<AxiosResponse<{ access_token: string; customer: any }>> {
  return api.post('/shop/customers/register', data);
}

export async function customerLogin(credentials: {
  email: string; password: string;
}): Promise<AxiosResponse<{ access_token: string; customer: any }>> {
  return api.post('/shop/customers/login', credentials);
}

export async function getCustomerProfile(): Promise<AxiosResponse<any>> {
  return api.get('/shop/customers/me');
}

export async function updateCustomerProfile(data: {
  first_name?: string; last_name?: string; phone?: string;
  default_address_line1?: string; default_address_line2?: string;
  default_city?: string; default_state?: string; default_zip?: string;
}): Promise<AxiosResponse<any>> {
  return api.patch('/shop/customers/me', data);
}

export async function changeCustomerPassword(data: {
  current_password: string; new_password: string;
}): Promise<AxiosResponse<any>> {
  return api.post('/shop/customers/me/change-password', data);
}

export async function getCustomerOrders(params?: {
  status?: 'active' | 'past'; page?: number; page_size?: number;
}): Promise<AxiosResponse<{
  orders: Array<{
    id: number; order_number: string; status: string; company_name?: string;
    company_logo?: string; item_count: number; subtotal: number; tax: number;
    shipping_cost: number; total: number;
    items: Array<{
      product_title: string; variant_title: string; variant_size?: string;
      quantity: number; unit_price: number; line_total: number; primary_image?: string;
    }>;
    created_at: string; estimated_delivery_start?: string; estimated_delivery_end?: string;
  }>;
  total: number; page: number; page_size: number; total_pages: number;
}>> {
  const token = localStorage.getItem('customer_access_token');
  return api.get('/shop/customers/me/orders', { params, headers: { Authorization: `Bearer ${token}` } });
}

// ===== Customer Addresses =====

export async function getCustomerAddresses(): Promise<AxiosResponse<{
  addresses: Array<{
    id: number; label: string; address_line1: string; address_line2?: string;
    city: string; state: string; zip: string; country: string; is_default: boolean; created_at: string;
  }>;
}>> {
  const token = localStorage.getItem('customer_access_token');
  return api.get('/shop/customers/me/addresses', { headers: { Authorization: `Bearer ${token}` } });
}

export async function createCustomerAddress(data: {
  label: string; address_line1: string; address_line2?: string;
  city: string; state: string; zip: string; country?: string; is_default?: boolean;
}): Promise<AxiosResponse<{
  id: number; label: string; address_line1: string; address_line2?: string;
  city: string; state: string; zip: string; country: string; is_default: boolean; created_at: string;
}>> {
  const token = localStorage.getItem('customer_access_token');
  return api.post('/shop/customers/me/addresses', data, { headers: { Authorization: `Bearer ${token}` } });
}

export async function updateCustomerAddress(addressId: number, data: {
  label?: string; address_line1?: string; address_line2?: string;
  city?: string; state?: string; zip?: string; country?: string; is_default?: boolean;
}): Promise<AxiosResponse<{
  id: number; label: string; address_line1: string; address_line2?: string;
  city: string; state: string; zip: string; country: string; is_default: boolean; created_at: string;
}>> {
  const token = localStorage.getItem('customer_access_token');
  return api.put(`/shop/customers/me/addresses/${addressId}`, data, { headers: { Authorization: `Bearer ${token}` } });
}

export async function deleteCustomerAddress(addressId: number): Promise<AxiosResponse<{ message: string }>> {
  const token = localStorage.getItem('customer_access_token');
  return api.delete(`/shop/customers/me/addresses/${addressId}`, { headers: { Authorization: `Bearer ${token}` } });
}

export async function setDefaultAddress(addressId: number): Promise<AxiosResponse<{ message: string }>> {
  const token = localStorage.getItem('customer_access_token');
  return api.post(`/shop/customers/me/addresses/${addressId}/set-default`, {}, { headers: { Authorization: `Bearer ${token}` } });
}

// ===== Coupons =====

export async function validateCoupon(code: string, subtotal: number, customerId?: number): Promise<AxiosResponse<{
  valid: boolean; code?: string; coupon_id?: number;
  discount_type?: 'percentage' | 'fixed_amount'; discount_value?: number;
  discount_amount?: number; new_subtotal?: number; error?: string; error_code?: string;
}>> {
  return api.post('/shop/coupons/validate', { code, subtotal, customer_id: customerId });
}

// ===== Wishlist =====

export async function getWishlist(): Promise<AxiosResponse<{
  items: Array<{
    id: number; sku: string; name: string; brand_name: string | null;
    price: number; compare_at_price: number | null; primary_image_url: string | null;
    available_to_sell: number; added_at: string;
  }>; count: number;
}>> {
  return api.get('/shop/customers/wishlist');
}

export async function addToWishlist(productId: number): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return api.post('/shop/customers/wishlist', { product_id: productId });
}

export async function addToWishlistBySku(sku: string): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return api.post('/shop/customers/wishlist/sku', { sku });
}

export async function removeFromWishlist(productId: number): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return api.delete(`/shop/customers/wishlist/${productId}`);
}

export async function removeFromWishlistBySku(sku: string): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return api.delete(`/shop/customers/wishlist/sku/${sku}`);
}

export async function mergeWishlist(skus: string[]): Promise<AxiosResponse<{
  merged_count: number; already_in_wishlist: number; not_found: string[];
}>> {
  return api.post('/shop/customers/wishlist/merge', { skus });
}

export async function checkWishlist(productId: number): Promise<AxiosResponse<{ in_wishlist: boolean }>> {
  return api.get(`/shop/customers/wishlist/check/${productId}`);
}

export async function getWishlistCount(): Promise<AxiosResponse<{ count: number }>> {
  return api.get('/shop/customers/wishlist/count');
}

// ===== Reviews =====

export async function getProductReviews(sku: string, page: number = 1, perPage: number = 10, sort: string = 'newest'): Promise<AxiosResponse<{
  reviews: Array<{
    id: number; customer_first_name: string | null; customer_last_initial: string | null;
    rating: number; title: string | null; body: string | null; is_verified_purchase: boolean;
    created_at: string;
    brand_response: { id: number; body: string; responder_name: string | null; created_at: string } | null;
  }>; total: number; page: number; page_size: number; total_pages: number;
}>> {
  return api.get(`/shop/products/${sku}/reviews`, { params: { page, page_size: perPage, sort } });
}

export async function getProductReviewStats(sku: string): Promise<AxiosResponse<{
  average_rating: number; total_reviews: number;
  rating_distribution: { five_star: number; four_star: number; three_star: number; two_star: number; one_star: number };
  verified_purchase_count: number;
}>> {
  return api.get(`/shop/products/${sku}/reviews/stats`);
}

export async function submitReview(data: {
  product_sku: string; rating: number; title?: string; body?: string;
}): Promise<AxiosResponse<{
  id: number; rating: number; title: string | null; body: string | null;
  is_verified_purchase: boolean; created_at: string;
}>> {
  return api.post('/shop/customers/reviews', data);
}

export async function getReviewableOrders(): Promise<AxiosResponse<{
  items: Array<{
    order_item_id: number; order_number: string; product_id: number;
    product_sku: string; product_name: string; product_image_url: string | null;
    purchased_at: string; has_existing_review: boolean;
  }>; count: number;
}>> {
  return api.get('/shop/customers/reviewable-orders');
}

// ===== Payments (shop checkout) =====

export async function createPaymentIntent(sessionId: string, options?: {
  zip_code?: string; fulfillment_method?: string;
}): Promise<AxiosResponse<{
  client_secret: string; payment_intent_id: string; publishable_key: string;
  amount: number; subtotal: number; shipping: number; tax: number;
}>> {
  return api.post('/payments/create-payment-intent', {
    session_id: sessionId,
    zip_code: options?.zip_code,
    fulfillment_method: options?.fulfillment_method || 'delivery',
  });
}

export async function updatePaymentIntent(data: {
  payment_intent_id: string; session_id: string; zip_code?: string;
  fulfillment_method: string; street_address?: string; city?: string; state?: string;
}): Promise<AxiosResponse<{
  amount: number; subtotal: number; shipping: number; tax: number; shipping_source?: string;
}>> {
  return api.post('/payments/update-payment-intent', data);
}

export async function prepareOrder(data: {
  session_id: string; payment_intent_id: string;
  shipping_address: { address: string; apartment?: string; city: string; state: string; zip_code: string; country?: string };
  customer_info: { email: string; first_name: string; last_name: string; phone?: string };
  discount_code?: string; fulfillment_method?: 'delivery' | 'pickup';
}): Promise<AxiosResponse<{ order_number: string; order_id: number; status: string }>> {
  return api.post('/payments/prepare-order', data);
}

export async function confirmPayment(data: {
  session_id: string; payment_intent_id: string;
  shipping_address: { address: string; apartment?: string; city: string; state: string; zip_code: string; country?: string };
  customer_info: { email: string; first_name: string; last_name: string; phone?: string };
  discount_code?: string; fulfillment_method?: 'delivery' | 'pickup';
}): Promise<AxiosResponse<Order>> {
  return api.post('/payments/confirm-payment', data);
}

export async function getPaymentConfig(): Promise<AxiosResponse<{ publishable_key: string; tax_enabled: boolean }>> {
  return api.get('/payments/config');
}

// ===== Driver =====

export async function getDriverOrders(status?: string): Promise<AxiosResponse<Array<{
  order_id: number; order_number: string; status: string; customer_name: string;
  address: string; phone: string; item_count: number; created_at: string;
}>>> {
  const params = status ? { status } : undefined;
  return api.get('/driver/orders', { params });
}

export async function updateDriverLocation(data: {
  order_id: number; latitude: number; longitude: number; accuracy?: number;
}): Promise<AxiosResponse<{
  success: boolean; order_number: string; eta_minutes: number | null;
  eta_display: string | null; distance_miles: number | null;
}>> {
  return api.post('/driver/location', data);
}

export async function startDelivery(orderId: number): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return api.post(`/driver/start-delivery/${orderId}`);
}

export async function markDelivered(orderId: number): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return api.post(`/driver/mark-delivered/${orderId}`);
}

export async function uploadDeliveryPhoto(orderId: number, data: {
  image_base64: string; content_type?: string;
}): Promise<AxiosResponse<{ success: boolean; photo_url: string; message: string }>> {
  return api.post(`/driver/upload-delivery-photo/${orderId}`, data);
}

export async function getCustomerETA(orderNumber: string): Promise<AxiosResponse<{
  order_number: string; status: string; driver_tracking_active: boolean;
  eta_minutes: number | null; eta_display: string | null; distance_miles: number | null;
  driver_nearby: boolean; last_update: string | null; message: string;
}>> {
  return api.get(`/driver/eta/${orderNumber}`);
}

// ===== Uber Direct Delivery =====

export async function dispatchUberDelivery(orderNumber: string): Promise<AxiosResponse<{
  order_number: string; delivery_id: string; tracking_url: string;
  status: string; fee_dollars: number; message: string;
}>> {
  return api.post(`/delivery/dispatch/${orderNumber}`);
}

export async function dispatchUberDeliveryTest(orderNumber: string): Promise<AxiosResponse<{
  order_number: string; delivery_id: string; tracking_url: string;
  status: string; fee_dollars: number; message: string;
}>> {
  return api.post(`/delivery/dispatch-test/${orderNumber}`);
}

export async function getUberDeliveryStatus(orderNumber: string): Promise<AxiosResponse<{
  order_number: string; uber_status: string; uber_tracking_url: string | null;
  courier_name: string | null; courier_phone: string | null;
  courier_photo_url: string | null; courier_vehicle_plate: string | null;
}>> {
  return api.get(`/delivery/status/${orderNumber}`);
}

// ===== Waitlist =====

export async function joinWaitlist(data: {
  email: string; full_name: string; zip_code: string; phone?: string;
}): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return api.post('/waitlist/join', data);
}

export async function joinVendorWaitlist(data: {
  company_name: string; company_category: string; first_name: string;
  last_name: string; email: string; phone?: string; instagram?: string;
}): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return api.post('/waitlist/vendor/join', data);
}

export async function redeemAccessToken(token: string): Promise<AxiosResponse<{ token: string; name: string }>> {
  return api.post(`/waitlist/access/${token}`);
}
